// src/components/chat/ToolProcessingSection.tsx

import * as React from "react";
import { useState, useEffect, useCallback, useRef } from "react";
import { createLogger } from "../../utils/logger";
import {
  TOOL_PROCESSING_MESSAGES,
  TOOL_MESSAGE_ROTATION_MS,
} from "../../config/toolProcessingMessages";

const log = createLogger("ToolProcessingSection");

/**
 * Props interface for the ToolProcessingSection component
 */
interface ToolProcessingSectionProps {
  /** Whether a tool is currently being processed */
  isProcessingTool: boolean;
  /** Optional CSS class name */
  className?: string;
  /**
   * Rotating status messages to display. Defaults to the generic
   * TOOL_PROCESSING_MESSAGES (auto-redline). Per-tool indicators (e.g.
   * add_comment) pass their own set.
   */
  messages?: readonly string[];
}

/**
 * ToolProcessingSection - Shows visual feedback during tool execution
 *
 * Displays an animated document icon with rotating status messages.
 * Appears immediately when tool_use_start event arrives and hides
 * when tool_use_result arrives or streaming completes.
 */
const ToolProcessingSection: React.FC<ToolProcessingSectionProps> = ({
  isProcessingTool,
  className = "",
  messages = TOOL_PROCESSING_MESSAGES,
}) => {
  // Track which message indices have been shown (for full rotation before repeat)
  const shownMessagesRef = useRef<Set<number>>(new Set());

  // Current rotating message - initialize with random selection
  const [currentMessage, setCurrentMessage] = useState<string>(() => {
    const randomIndex = Math.floor(Math.random() * messages.length);
    shownMessagesRef.current.add(randomIndex);
    return messages[randomIndex];
  });

  // Ref for message rotation timeout
  const rotationTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  // Log component mount for debugging
  useEffect(() => {
    log.info("ToolProcessingSection mounted", { isProcessingTool });
  }, []);

  // Reset tracking when processing stops
  useEffect(() => {
    if (isProcessingTool) {
      log.debug("Tool processing started");
    } else {
      // Reset shown messages for next activation (fresh rotation cycle)
      shownMessagesRef.current.clear();
      log.debug("Tool processing completed, message tracking reset");
    }
  }, [isProcessingTool]);

  // Pick random message from unseen pool (full rotation before repeat)
  const pickRandomMessage = useCallback((): string => {
    const messagesCount = messages.length as number;
    if (messagesCount <= 1) {
      return messages[0];
    }

    // Get unseen message indices
    const allIndices = Array.from({ length: messagesCount }, (_, i) => i);
    const unseenIndices = allIndices.filter((i) => !shownMessagesRef.current.has(i));

    let selectedIndex: number;

    if (unseenIndices.length > 0) {
      // Select from unseen only
      selectedIndex = unseenIndices[Math.floor(Math.random() * unseenIndices.length)];
      log.debug("Selected unseen message", {
        selectedIndex,
        unseenCount: unseenIndices.length,
        totalMessages: messagesCount,
      });
    } else {
      // All shown - reset cycle and select randomly
      shownMessagesRef.current.clear();
      selectedIndex = Math.floor(Math.random() * messagesCount);
      log.info("All messages shown, reset cycle", {
        selectedIndex,
        totalMessages: messagesCount,
      });
    }

    // Mark as shown
    shownMessagesRef.current.add(selectedIndex);

    return messages[selectedIndex];
  }, [messages]);

  // Message rotation effect (only runs when array has multiple items)
  useEffect(() => {
    const messagesCount = messages.length as number;
    if (!isProcessingTool || messagesCount <= 1) {
      return;
    }

    log.debug("Starting message rotation");

    const performRotation = () => {
      setCurrentMessage(pickRandomMessage());
      rotationTimeoutRef.current = setTimeout(performRotation, TOOL_MESSAGE_ROTATION_MS);
    };

    // Schedule first rotation after initial delay
    rotationTimeoutRef.current = setTimeout(performRotation, TOOL_MESSAGE_ROTATION_MS);

    return () => {
      if (rotationTimeoutRef.current) {
        clearTimeout(rotationTimeoutRef.current);
        rotationTimeoutRef.current = null;
      }
    };
  }, [isProcessingTool, pickRandomMessage, messages]);

  // Don't render when not processing
  if (!isProcessingTool) {
    return null;
  }

  return (
    <div className={className} style={{ marginBottom: "8px" }}>
      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: "12px",
          padding: "8px 12px",
          backgroundColor: "#F8F9FA",
          borderRadius: "8px",
          border: "1px solid #E9ECEF",
          fontFamily: "'Segoe UI', sans-serif",
        }}
      >
        {/* Message content with icon */}
        <div
          style={{
            flex: 1,
            fontSize: "13px",
            color: "#495057",
            fontWeight: 500,
            display: "flex",
            alignItems: "center",
            gap: "6px",
          }}
        >
          {/* Animated document icon */}
          <svg width="28" height="26" viewBox="0 0 100 100" style={{ flexShrink: 0 }}>
            <style>
              {`
                @keyframes draw-line {
                  0% { stroke-dashoffset: 30; opacity: 0.3; }
                  50% { stroke-dashoffset: 0; opacity: 1; }
                  100% { stroke-dashoffset: -30; opacity: 0.3; }
                }
                .doc-line-1 { animation: draw-line 2s ease-in-out infinite; }
                .doc-line-2 { animation: draw-line 2s ease-in-out infinite 0.3s; }
                .doc-line-3 { animation: draw-line 2s ease-in-out infinite 0.6s; }
              `}
            </style>

            {/* Document outline with folded corner */}
            <path
              d="M25 10 L25 90 L75 90 L75 30 L55 10 Z"
              fill="none"
              stroke="#106ebe"
              strokeWidth="3"
              strokeLinejoin="round"
            />
            {/* Folded corner */}
            <path
              d="M55 10 L55 30 L75 30"
              fill="none"
              stroke="#106ebe"
              strokeWidth="3"
              strokeLinejoin="round"
            />

            {/* Static line (existing content) */}
            <line x1="35" y1="45" x2="60" y2="45" stroke="#106ebe" strokeWidth="2" opacity="0.3" />

            {/* Animated writing lines */}
            <line
              className="doc-line-1"
              x1="35"
              y1="55"
              x2="65"
              y2="55"
              stroke="#106ebe"
              strokeWidth="2.5"
              strokeDasharray="30 30"
            />
            <line
              className="doc-line-2"
              x1="35"
              y1="65"
              x2="58"
              y2="65"
              stroke="#106ebe"
              strokeWidth="2.5"
              strokeDasharray="23 23"
            />
            <line
              className="doc-line-3"
              x1="35"
              y1="75"
              x2="52"
              y2="75"
              stroke="#106ebe"
              strokeWidth="2.5"
              strokeDasharray="17 17"
            />
          </svg>
          {currentMessage}
        </div>
      </div>
    </div>
  );
};

export default ToolProcessingSection;
